import { styled, Theme } from "@mui/material";
import Avatar, { AvatarProps } from "../Avatar";

interface OverlapProps {
  size: "xs" | "s" | "m" | "l" | "xl" | "xxl";
}

const getOverlap = (size: AvatarProps["size"] = "xl", theme: Theme) => {
  const OVERLAP_MAP: Record<NonNullable<AvatarProps["size"]>, string> = {
    xs: `-${theme.wsc.spacing[0]}`,
    s: `-${theme.wsc.spacing[0]}`,
    m: `calc(${theme.wsc.spacing[0]} * -1.5)`,
    l: `-${theme.wsc.spacing[1]}`,
    xl: `calc(${theme.wsc.spacing[1]} * -1.5)`,
    xxl: `-${theme.wsc.spacing[2]}`,
  };

  return OVERLAP_MAP[size];
};

const getBorderWidth = (size: AvatarProps["size"] = "xl") => {
  const BORDER_WIDTH_MAP: Record<NonNullable<AvatarProps["size"]>, number> = {
    xs: 1,
    s: 1,
    m: 1,
    l: 2,
    xl: 2,
    xxl: 2,
  };

  return BORDER_WIDTH_MAP[size];
};

export const StyledAvatarGroup = styled("div")`
  display: flex;
  align-items: center;
`;

export const StyledAvatarWrapper = styled("span")<OverlapProps>(
  ({ theme, size }) => `
    margin-right: ${getOverlap(size, theme)};

    &:last-of-type {
      margin-right: 0;
    }
  `,
);

export const StyledAvatar = styled(Avatar)(
  ({ theme, size, isHighlighted }) => `
    margin-right: ${getOverlap(size, theme)};
    border: ${getBorderWidth(size)}px solid ${
      isHighlighted
        ? theme.wsc.palette.success[100]
        : theme.wsc.palette.neutral[600]
    };
    
    &:last-of-type {
      margin-right: 0;
    }
  `,
);

export const StyledIndicator = styled("div")<OverlapProps>(({
  size,
  theme,
}) => {
  const sizeMap = {
    xs: `calc(${theme.wsc.spacing[2]} * 1)`,
    s: `calc(${theme.wsc.spacing[2]} * 1.5)`,
    m: `calc(${theme.wsc.spacing[2]} * 2.5)`,
    l: `calc(${theme.wsc.spacing[2]} * 3.75)`,
    xl: `calc(${theme.wsc.spacing[2]} * 5)`,
    xxl: `calc(${theme.wsc.spacing[2]} * 6.245)`,
  };
  const fontSizeMap = {
    xs: theme.wsc.typography.body2bold.fontSize,
    s: theme.wsc.typography.body2bold.fontSize,
    m: theme.wsc.typography.header5.fontSize,
    l: theme.wsc.typography.header3.fontSize,
    xl: theme.wsc.typography.header2.fontSize,
    xxl: theme.wsc.typography.header2.fontSize,
  };
  const paddingMap = {
    xs: theme.wsc.spacing[0],
    s: theme.wsc.spacing[0],
    m: theme.wsc.spacing[1],
    l: theme.wsc.spacing[2],
    xl: theme.wsc.spacing[3],
    xxl: theme.wsc.spacing[4],
  };
  const indicatorSize = sizeMap[size] || sizeMap["xl"];

  return `
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: ${indicatorSize};
      height: ${indicatorSize};
      margin-left: ${getOverlap(size, theme)};
      border-radius: 999px;
      background: ${theme.wsc.palette.neutral[700]};
      border: ${getBorderWidth(size)}px solid ${theme.wsc.palette.neutral[600]};
      color: ${theme.wsc.palette.neutral[200]};
      font-weight: ${theme.wsc.typography.header5.fontWeight};
      z-index: 1;
      box-sizing: border-box;
      font-size: ${fontSizeMap[size]};
      padding: 0 ${paddingMap[size]};
    `;
});

export const StyledIndicatorTooltipTitle = styled("div")`
  display: flex;
  flex-direction: column;
`;
